import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceArea,
} from 'recharts';
import { ArrowTrendingUpIcon, DocumentIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { formatDate } from 'shared_items/formatConventions';
import { formatCompactCurrencyValue as formatValue } from '../../viewServices/assets_helpers';

const BASE_LABEL = 'Base';
const MARKET_LABEL = 'Market';
const MIN_ZOOM_POINTS = 2;

const tooltipSeriesLabel = (name) => {
  if (name === 'total' || name === BASE_LABEL) return BASE_LABEL;
  if (name === 'total_market' || name === MARKET_LABEL) return MARKET_LABEL;
  return name;
};

const normalizeDateRange = (left, right) => {
  if (!left || !right) return null;
  if (left <= right) return { start: left, end: right };
  return { start: right, end: left };
};

const countPointsInRange = (rows, start, end) =>
  rows.filter((row) => row.snapshot_date >= start && row.snapshot_date <= end).length;

const filterHistoryByRange = (rows, start, end) =>
  rows.filter((row) => row.snapshot_date >= start && row.snapshot_date <= end);

const historySignature = (rows) =>
  rows.map((row) => `${row.snapshot_date}:${row.total}:${row.total_market}`).join('|');

const CardEvolutionHistory = ({ history, loading }) => {
  const [zoomRange, setZoomRange] = useState(null);
  const [refAreaLeft, setRefAreaLeft] = useState('');
  const [refAreaRight, setRefAreaRight] = useState('');
  const [isSelecting, setIsSelecting] = useState(false);
  const historyRef = useRef(history);

  useEffect(() => {
    historyRef.current = history;
  }, [history]);

  const historyKey = useMemo(() => historySignature(history), [history]);

  useEffect(() => {
    setZoomRange(null);
    setRefAreaLeft('');
    setRefAreaRight('');
    setIsSelecting(false);
  }, [historyKey]);

  const chartData = useMemo(() => {
    if (!zoomRange) return history;
    return filterHistoryByRange(history, zoomRange.start, zoomRange.end);
  }, [history, zoomRange]);

  const isZoomed = !!zoomRange;
  const canBrush = history.length >= MIN_ZOOM_POINTS;

  const clearZoom = useCallback(() => {
    setZoomRange(null);
    setRefAreaLeft('');
    setRefAreaRight('');
    setIsSelecting(false);
  }, []);

  const finishSelection = useCallback(() => {
    if (!isSelecting) return;

    const range = normalizeDateRange(refAreaLeft, refAreaRight);
    setIsSelecting(false);
    setRefAreaLeft('');
    setRefAreaRight('');

    if (!range || range.start === range.end) return;

    const pointCount = countPointsInRange(historyRef.current, range.start, range.end);
    if (pointCount >= MIN_ZOOM_POINTS) {
      setZoomRange(range);
    }
  }, [isSelecting, refAreaLeft, refAreaRight]);

  useEffect(() => {
    if (!isSelecting) return undefined;

    const onDocumentMouseUp = () => finishSelection();

    document.addEventListener('mouseup', onDocumentMouseUp);
    return () => document.removeEventListener('mouseup', onDocumentMouseUp);
  }, [isSelecting, finishSelection]);

  const handleMouseDown = (state) => {
    if (!canBrush || !state?.activeLabel) return;
    if (typeof state?.activeLabel !== 'string') return;
    setIsSelecting(true);
    setRefAreaLeft(state.activeLabel);
    setRefAreaRight(state.activeLabel);
  };

  const handleMouseMove = (state) => {
    if (!isSelecting || !state?.activeLabel) return;
    setRefAreaRight(state.activeLabel);
  };

  const handleMouseUp = () => {
    finishSelection();
  };

  const showReferenceArea =
    isSelecting && refAreaLeft && refAreaRight && refAreaLeft !== refAreaRight;

  return (
    <div className="bg-white shadow rounded-xl overflow-hidden p-3">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <ArrowTrendingUpIcon className="h-5 w-5 flex-shrink-0 text-gray-500" />
          <span className="text-md font-semibold text-gray-800">Evolution</span>
          {isZoomed && (
            <span className="truncate text-xs text-gray-400">
              {formatDate(zoomRange.start, 'display')} – {formatDate(zoomRange.end, 'display')}
            </span>
          )}
        </div>
        {isZoomed && (
          <button
            type="button"
            onClick={clearZoom}
            className="shrink-0 rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label="Reset chart zoom"
            title="Show full period"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        )}
      </div>
      <div>
        {loading ? (
          <div className="h-40 animate-pulse rounded bg-gray-100" />
        ) : history.length < MIN_ZOOM_POINTS ? (
          <div className="flex h-40 items-center justify-center">
            <div className="text-center">
              <DocumentIcon className="mx-auto mb-3 h-16 w-16 text-gray-400" />
              <div className="text-sm font-medium text-gray-500">
                At least 2 snapshots needed to show evolution.
              </div>
            </div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={180} className={canBrush ? 'select-none' : ''}>
            <LineChart
              data={chartData}
              margin={{ top: 8, right: 16, bottom: 4, left: 0 }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              style={{ cursor: canBrush && !isZoomed ? 'crosshair' : 'default' }}
            >
              <XAxis
                dataKey="snapshot_date"
                tickFormatter={(v) => formatDate(v, 'chart')}
                tick={{ fontSize: 10, fill: '#6b7280' }}
                interval="preserveStartEnd"
                allowDataOverflow
              />
              <YAxis
                tickFormatter={(v) => formatValue(v)}
                tick={{ fontSize: 10, fill: '#6b7280' }}
                width={52}
                allowDataOverflow
              />
              <Tooltip
                labelFormatter={(label) => formatDate(label, 'display')}
                formatter={(value, name) => [
                  `R$ ${formatValue(value)}`,
                  tooltipSeriesLabel(name),
                ]}
                contentStyle={{ fontSize: 12 }}
              />
              {showReferenceArea && (
                <ReferenceArea
                  x1={refAreaLeft}
                  x2={refAreaRight}
                  stroke="#6b7280"
                  strokeOpacity={0.4}
                  fill="#9ca3af"
                  fillOpacity={0.2}
                />
              )}
              <Line
                type="monotone"
                dataKey="total"
                name={BASE_LABEL}
                stroke="#6b7280"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, fill: '#6b7280' }}
                isAnimationActive={false}
              />
              <Line
                type="monotone"
                dataKey="total_market"
                name={MARKET_LABEL}
                stroke="#166534"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, fill: '#166534' }}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default CardEvolutionHistory;
